import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { currencyCode } from 'Helpers/Geo/data';
import ItemList from 'Components/Product/Seo/ItemList';
import useViewport from 'Hooks/useViewport';
import pricing from 'Containers/StaticSearch/games-under-pricing';
import SkeletonHitCard from 'Components/Cards/SkeletonHitCard';
import HitCard from 'Components/Cards/HitCard';
import PriceContainer from 'Containers/Utility/Price/PriceContainer';
import SpecialFormattedNumber from 'Containers/Utility/Price/SpecialFormattedNumber';

export default function PopularCategories({
  trendingHits,
  newReleaseHits,
  mostWishedHits,
  comingSoonHits,
  gamesUnderFiveHits,
}) {
  const [tab, setTab] = useState('cheap');

  const { isBelowLg, isAboveMd } = useViewport();

  let sliceLimit = 12;
  if (isBelowLg) sliceLimit = 10;

  let popularProducts = gamesUnderFiveHits;
  let viewAllLink = '/games-under/5';

  if (tab === 'cheap') {
    popularProducts = gamesUnderFiveHits;
    viewAllLink = '/games-under/5';
  }
  if (tab === 'trending') {
    popularProducts = trendingHits;
    viewAllLink = '/trending-deals';
  }
  if (tab === 'newreleases') {
    popularProducts = newReleaseHits;
    viewAllLink = '/new-releases';
  }
  if (tab === 'mostwanted') {
    popularProducts = mostWishedHits;
    viewAllLink = '/most-wanted';
  }
  if (tab === 'upcoming') {
    popularProducts = comingSoonHits;
    viewAllLink = '/upcoming-games';
  }

  const maxPrice = pricing['5'].max;

  return (
    <section className="container ProductTabsSection">
      <div className="ProductTabsSection__titleView">
        <h2>
          <FormattedMessage
            id="popular-categories.title.popular-categories"
            defaultMessage="Popular Categories"
          />
        </h2>
        {!isAboveMd &&
          <Link to={viewAllLink} className="ProductTabsSection__viewLink">
            <FormattedMessage
              id="popular-categories.link.view-all"
              defaultMessage="View all"
            />
          </Link>
        }
      </div>

      <div className="ProductTabsSection__tabsView">
        <div className="ProductTabsSection__tabs">
          <button className={classNames('ProductTabsSection__tabs__tab', { 'ProductTabsSection__tabs__tab--active': tab === 'cheap' })} onClick={() => { setTab('cheap'); }}>
            <FormattedMessage id="popular-categorie.tab.games" defaultMessage="Games Under {number}" values={{ number: <SpecialFormattedNumber hideDecimals value={maxPrice[currencyCode] / 100} style="currency" /> }} />
          </button>
          <ItemList hits={gamesUnderFiveHits} title="Games Under $5" />

          <button className={classNames('ProductTabsSection__tabs__tab', { 'ProductTabsSection__tabs__tab--active': tab === 'trending' })} onClick={() => { setTab('trending'); }}>
            <FormattedMessage id="popular-categorie.tab.all" defaultMessage="Trending Deals" />
          </button>
          <ItemList hits={trendingHits} title="Trending Deals" />

          <button className={classNames('ProductTabsSection__tabs__tab', { 'ProductTabsSection__tabs__tab--active': tab === 'newreleases' })} onClick={() => { setTab('newreleases'); }}>
            <FormattedMessage id="popular-categorie.tab.game-bundles" defaultMessage="New Releases" />
          </button>
          <ItemList hits={newReleaseHits} title="New Releases" />

          <button className={classNames('ProductTabsSection__tabs__tab', { 'ProductTabsSection__tabs__tab--active': tab === 'upcoming' })} onClick={() => { setTab('upcoming'); }}>
            <FormattedMessage id="popular-categorie.tab.books-comics" defaultMessage="Upcoming Games" />
          </button>
          <ItemList hits={comingSoonHits} title="Upcoming Games" />

          <button className={classNames('ProductTabsSection__tabs__tab', { 'ProductTabsSection__tabs__tab--active': tab === 'mostwanted' })} onClick={() => { setTab('mostwanted'); }}>
            <FormattedMessage id="popular-categorie.tab.most-wanted" defaultMessage="Most Wanted" />
          </button>
          <ItemList hits={mostWishedHits} title="Most Wanted" />
        </div>

        {isAboveMd &&
          <Link to={viewAllLink} className="ProductTabsSection__viewLink">
            <FormattedMessage
              id="popular-categorie.link.view-all"
              defaultMessage="View all"
            />
          </Link>
        }
      </div>

      {isEmpty(popularProducts) &&
        <div className="row">
          <SkeletonHitCard
            numberOfCards={sliceLimit}
            xsSize={6}
            mdSize={6}
            lgSize={3}
          />
        </div>
      }

      <ul className="HitCardsRow HitCardsRow--marginReset row">
        {popularProducts.slice(0, sliceLimit).map(hit =>
          <HitCard
            key={hit.objectID}
            hit={hit}
            xsSize={6}
            smSize={6}
            mdSize={6}
            lgSize={3}
            hitCardContainerTag="li"
            hitCardNameTag="h3"
          >
            <PriceContainer
              discountPercent={hit.discount_percent}
              price={hit.price}
              tiered={hit.tiered}
              slug={hit.slug}
              hideDiscount={hit.hide_discount}
              payWhatYouWant={hit.pay_what_you_want}
            />
          </HitCard>,
        )}
      </ul>
    </section>
  );
}

PopularCategories.propTypes = {
  trendingHits: PropTypes.arrayOf(PropTypes.object),
  newReleaseHits: PropTypes.arrayOf(PropTypes.object),
  mostWishedHits: PropTypes.arrayOf(PropTypes.object),
  comingSoonHits: PropTypes.arrayOf(PropTypes.object),
  gamesUnderFiveHits: PropTypes.arrayOf(PropTypes.object),
};

PopularCategories.defaultProps = {
  trendingHits: [],
  newReleaseHits: [],
  mostWishedHits: [],
  comingSoonHits: [],
  gamesUnderFiveHits: [],
};
